import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMapPin, FiPhone, FiMail, FiSend, FiPrinter, FiBriefcase, FiCheckCircle } from 'react-icons/fi';
import { useMessagesData } from './context/MessagesContext';
import { useContactData } from './context/ContactContext';

export default function ContactPage() {
  const { sections, loading: sectionsLoading } = useContactData();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSent, setIsSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { addMessage } = useMessagesData();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addMessage(formData);
      setFormData({ name: '', email: '', message: '' });
      setIsSent(true);
      setTimeout(() => setIsSent(false), 5000);
    } catch (error) {
      alert("Une erreur est survenue lors de l'envoi du message.");
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (section) => {
    const { type, title, subtitle, data, content } = section;

    switch (type) {
      case 'hero':
        const heroButtons = (Array.isArray(data) && data.length > 0) ? data : [
          { label: 'Services', link: '/services' },
          { label: 'Métiers', link: '/metiers' }
        ];

        return (
          <div className="services-hero" key={section.id}>
            <div className="services-hero-illus" aria-hidden="true" />
            <div className="services-hero-inner">
              <h1 className="services-hero-title">
                {!title ? (
                  <span className="accent">Contact</span>
                ) : title.includes(':') ? (
                  <>
                    {title.split(':')[0]} : <span className="accent">{title.split(':')[1]}</span>
                  </>
                ) : (
                  <span className="accent">{title}</span>
                )}
              </h1>
              <p className="services-hero-subtitle">
                {subtitle || 'Parlez-nous de vos enjeux, de vos projets ou de vos contraintes actuelles.'}
              </p>
              <div className="services-cta" style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                {heroButtons.map((btn, bIdx) => (
                  <Link key={bIdx} className="btn btn-secondary" to={btn.link}>{btn.label}</Link>
                ))}
              </div>
            </div>
          </div>
        );

      case 'info_form':
        const contactInfo = data || {};
        const displayAddress = contactInfo.address || "35, rue Pierre Nicole\n75005 Paris";
        const displayPhone = contactInfo.phone_info || "01 42 36 03 50";
        const displayEmail = contactInfo.email_info || "info@version-net.com";

        return (
          <div className="section-inner contact-details-container" key={section.id}>
            <div className="contact-area-blob blob-1"></div>
            <div className="contact-area-blob blob-2"></div>
            <div className="contact-main-grid-glass">
              <div className="contact-info-card-glass glass-premium-effect">
                <h2 className="glass-card-title">{title || 'Coordonnées'}</h2>
                <div className="glass-info-stack">
                  <div className="glass-info-item">
                    <div className="glass-icon-circle"><FiMapPin /></div>
                    <div className="glass-info-text">
                      <h3>Adresse</h3>
                      <p style={{ whiteSpace: 'pre-line' }}>{displayAddress}</p>
                    </div>
                  </div>
                  <div className="glass-info-item">
                    <div className="glass-icon-circle"><FiPhone /></div>
                    <div className="glass-info-text">
                      <h3>Téléphone</h3>
                      <a href={`tel:${displayPhone.replace(/\s/g, '')}`} className="glass-link">{displayPhone}</a>
                    </div>
                  </div>
                  <div className="glass-info-item">
                    <div className="glass-icon-circle"><FiMail /></div>
                    <div className="glass-info-text">
                      <h3>Mail</h3>
                      <a href={`mailto:${displayEmail}`} className="glass-link">{displayEmail}</a>
                    </div>
                  </div>
                </div>
              </div>

              <form className="contact-form-glass glass-premium-effect" onSubmit={handleSubmit}>
                <h2 className="glass-card-title">Écrivez-nous</h2>
                <div className="glass-form-grid">
                  <div className="glass-input-group">
                    <label htmlFor="contact-name">Nom et prénom</label>
                    <input
                      id="contact-name" type="text" placeholder="Votre nom et prénom" required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={loading}
                    />
                  </div>
                  <div className="glass-input-group">
                    <label htmlFor="contact-email">Email</label>
                    <input
                      id="contact-email" type="email" placeholder="vous@exemple.com" required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      disabled={loading}
                    />
                  </div>
                  <div className="glass-input-group full-width">
                    <label htmlFor="contact-message">Message</label>
                    <textarea
                      id="contact-message" rows="5" placeholder="Parlez-nous de votre projet" required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      disabled={loading}
                    />
                  </div>
                </div>
                {isSent && (
                  <div className="glass-form-success">
                    <FiCheckCircle /> Merci ! Votre message a bien été envoyé.
                  </div>
                )}
                <button type="submit" className="glass-btn-submit" disabled={loading}>
                  <span>{loading ? 'Envoi...' : 'Envoyer'}</span>
                  {!loading && <FiSend className="glass-btn-icon" />}
                </button>
              </form>
            </div>
          </div>
        );

      case 'service_tiles':
        return (
          <div className="section-inner contact-services-container" key={section.id}>
            <div className="contact-services-grid">
              {(data || []).map((svc, idx) => (
                <div className="glass-premium-effect service-tile" key={idx}>
                  <h3 className="service-name">{svc.title}</h3>
                  <div className="service-details-list">
                    {/* TÉLÉPHONES */}
                    {(svc.phones || []).filter(p => p).map((phone, pIdx) => (
                      <div className="s-detail" key={`phone-${pIdx}`}>
                        <FiPhone /> <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
                      </div>
                    ))}

                    {/* FAX */}
                    {svc.fax && (
                      <div className="s-detail">
                        <FiPrinter /> <span>{svc.fax} (Fax)</span>
                      </div>
                    )}

                    {/* EMAILS */}
                    {(svc.mails || []).filter(m => m).map((mail, mIdx) => (
                      <div className="s-detail" key={`mail-${mIdx}`}>
                        <FiMail /> <a href={`mailto:${mail}`}>{mail}</a>
                      </div>
                    ))}

                    {/* DESCRIPTION COMPLÉMENTAIRE */}
                    {svc.description && (
                      <div className="s-detail description-field" style={{ marginTop: '0.5rem', opacity: 0.8, fontSize: '0.9rem', fontStyle: 'italic' }}>
                        <FiBriefcase /> <span>{svc.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'map':
        const mapUrl = section.btn_link || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.5471900115043!2d2.337775576887556!3d48.840428401452934!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e671c114cbffff%3A0x7c7a1e340903cce2!2s35%20Rue%20Pierre%20Nicole%2C%2075005%20Paris%2C%20France!5e0!3m2!1sfr!2sus!4v1700000000000!5m2!1sfr!2sus";

        return (
          <div className="section-inner contact-map-container-modern" key={section.id}>
            <div className="map-frame-glass glass-premium-effect">
              <iframe
                title="Localisation Version Net"
                src={mapUrl}
                width="100%" height="450" style={{ border: 0, borderRadius: '16px' }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <div className="map-footer-cta">
                <a href={mapUrl.replace('/embed', '')} target="_blank" rel="noopener noreferrer" className="glass-link-btn">
                  <FiMapPin /> Ouvrir dans Google Maps
                </a>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section id="contact" className="section contact-section-wrapper">
      <div className="section-inner">
        <nav className="breadcrumb" aria-label="Fil d'Ariane">
          <Link to="/">Accueil</Link>
          <span>/</span>
          <span>Contact</span>
        </nav>
      </div>

      {sections.length > 0 ? (
        sections.map(section => renderSection(section))
      ) : !sectionsLoading ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '4rem' }}>
          <p>La page contact est en cours de configuration.</p>
        </div>
      ) : null}
    </section>
  );
}
